import {
  GET_FORMATTED_CITATIONSV2,
  GET_STYLES_V2,
} from '@chegg-wt/core/oneGraphQueries/FormattedCitations'
import {
  FEEDBACK_REASON,
  CREATE_PROOF_READER_PAPER,
  GET_PAPER,
  GET_PAPER_LIST,
  PAPER_CHECK,
  SENTENCE_CHECK,
  SPLIT_TEXT_INTO_SENTENCES,
  UPDATE_PROOF_READER_PAPER,
} from '@chegg-wt/core/oneGraphQueries/Paraphraser'
import { getActualStyleId, popularStyles } from '@chegg-wt/core/utils/styles'
import {
  ExpressKeys,
  ExpressKeys_expressKeys,
} from '@chegg-wt/core/oneGraphQueries/__generated__/ExpressKeys'
import { EXPRESS_KEYS } from '@chegg-wt/core/oneGraphQueries/ExpressKeys'
import {
  SentenceCheck,
  SentenceCheckVariables,
} from '@chegg-wt/core/oneGraphQueries/__generated__/SentenceCheck'
import {
  GetPaper,
  GetPaperVariables,
} from '@chegg-wt/core/oneGraphQueries/__generated__/GetPaper'
import {
  GetPaperList,
  GetPaperListVariables,
} from '@chegg-wt/core/oneGraphQueries/__generated__/GetPaperList'
import {
  FeedbackReason,
  FeedbackReasonVariables,
} from '@chegg-wt/core/oneGraphQueries/__generated__/FeedbackReason'
import {
  SplitTextIntoSentences,
  SplitTextIntoSentencesVariables,
} from '@chegg-wt/core/oneGraphQueries/__generated__/SplitTextIntoSentences'
import {
  CreateProofReaderPaper,
  CreateProofReaderPaper_createProofReaderPaper,
  CreateProofReaderPaperVariables,
} from 'core/oneGraphQueries/__generated__/CreateProofReaderPaper'
import {
  UpdateProofReaderPaper,
  UpdateProofReaderPaper_updateProofReaderPaper,
  UpdateProofReaderPaperVariables,
} from 'core/oneGraphQueries/__generated__/UpdateProofReaderPaper'
import {
  GetFormattedCitationsV2,
  GetFormattedCitationsV2Variables,
} from '../oneGraphQueries/__generated__/GetFormattedCitationsV2'
import {
  GetCitationStylesV2,
  GetCitationStylesV2Variables,
} from '../oneGraphQueries/__generated__/GetCitationStylesV2'
import {
  PaperCheck,
  PaperCheck_paperCheck,
  PaperCheckVariables,
} from '../oneGraphQueries/__generated__/PaperCheck'
import { client } from './graphApi'

export const oneGraph = {
  getCitationsV2: (variables: GetFormattedCitationsV2Variables) => {
    return client.query<GetFormattedCitationsV2>({
      query: GET_FORMATTED_CITATIONSV2,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })
  },
  getStylesV2: ({ keyword, page = 1 }: GetCitationStylesV2Variables) => {
    return client.query<GetCitationStylesV2>({
      query: GET_STYLES_V2,
      variables: {
        keyword,
        page,
      },
      context: {
        clientName: 'oneGraph',
      },
    })
  },
  getExpressKeys: async (): Promise<ExpressKeys_expressKeys> => {
    try {
      const { data, errors } = await client.query<ExpressKeys>({
        query: EXPRESS_KEYS,
        context: {
          clientName: 'oneGraph',
        },
      })

      if (errors && errors.length > 0) {
        throw new Error('Could not getKeys while creating new channel')
      }
      return data.expressKeys
    } catch {
      throw new Error('Could not getKeys while creating new channel')
    }
  },

  paperCheck: async (variables: PaperCheckVariables): Promise<PaperCheck_paperCheck> => {
    const { data, errors } = await client.mutate<PaperCheck>({
      mutation: PAPER_CHECK,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors && errors.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in paperCheck mutation: ${errorMessages}`)
    }

    return data.paperCheck
  },

  createProofReaderPaper: async (
    variables: CreateProofReaderPaperVariables
  ): Promise<CreateProofReaderPaper_createProofReaderPaper> => {
    const { data, errors } = await client.mutate<CreateProofReaderPaper>({
      mutation: CREATE_PROOF_READER_PAPER,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors && errors.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in createProofReaderPaper mutation: ${errorMessages}`)
    }

    return data.createProofReaderPaper
  },

  updateProofReaderPaper: async (
    variables: UpdateProofReaderPaperVariables
  ): Promise<UpdateProofReaderPaper_updateProofReaderPaper> => {
    const { data, errors } = await client.mutate<UpdateProofReaderPaper>({
      mutation: UPDATE_PROOF_READER_PAPER,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors && errors.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in updateProofReaderPaper mutation: ${errorMessages}`)
    }

    return data.updateProofReaderPaper
  },

  sentenceCheck: async (variables: SentenceCheckVariables) => {
    const { data, errors } = await client.mutate<SentenceCheck>({
      mutation: SENTENCE_CHECK,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors && errors.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in sentenceCheck mutation: ${errorMessages}`)
    }
    return data.sentenceCheck
  },
  getPaper: async (variables: GetPaperVariables) => {
    const { data, errors } = await client.query<GetPaper>({
      query: GET_PAPER,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors?.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in getPaper query: ${errorMessages}`)
    }
    return data.getPaper
  },
  getPaperList: async (variables: GetPaperListVariables) => {
    const { data, errors } = await client.query<GetPaperList>({
      query: GET_PAPER_LIST,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors?.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in getPaperList query: ${errorMessages}`)
    }
    return data.getPaperList
  },
  feedbackReason: async (variables: FeedbackReasonVariables) => {
    const { data, errors } = await client.query<FeedbackReason>({
      query: FEEDBACK_REASON,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })
    if (errors?.length > 0) {
      const errorMessages = errors.map((error) => `${error.message} \n`).join(', ')
      throw new Error(`Error in feedback reason query: ${errorMessages}`)
    }
    return data.feedbackReason
  },
  splitTextIntoSentences: async (variables: SplitTextIntoSentencesVariables) => {
    const { data, errors } = await client.query<SplitTextIntoSentences>({
      query: SPLIT_TEXT_INTO_SENTENCES,
      variables,
      context: {
        clientName: 'oneGraph',
      },
    })

    if (errors?.length > 0) {
      const errorMessages = errors.map((error) => error.message).join(',\n')
      throw new Error(`Error in splitTextIntoSentences query: ${errorMessages}`)
    }

    return data.splitTextIntoSentences
  },
}

export const getUrlStyleNameV2 = async (style: string, page = 1) => {
  const actualStyleId = getActualStyleId(style)
  const pop = popularStyles[actualStyleId]
  if (pop) {
    return pop
  }
  try {
    const stylesV2Response = await oneGraph.getStylesV2({ keyword: actualStyleId, page })
    const { name } = stylesV2Response.data.citationStylesV2.results[0]
    return [name, name]
  } catch {
    const name = style.toUpperCase()
    return [name, name]
  }
}
